import { Mic, Send, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

export const LandingHero = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();

  const handleRegister = () => {
    setIsDialogOpen(false);
    navigate("/auth?tab=signup");
  };

  const handleLogin = () => {
    setIsDialogOpen(false);
    navigate("/auth?tab=login");
  };

  return (
    <>
      <section className="relative w-full min-h-[500px] md:min-h-[576px] flex flex-col items-center justify-center py-16 md:py-16 px-4 overflow-hidden">
        {/* Aqueous Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-[#D8DEE9]/10 to-white -z-10"></div>

        {/* Floating Blobs */}
        <div
          className="absolute top-20 left-[10%] w-[400px] h-[400px] rounded-full opacity-20 blur-3xl -z-10"
          style={{
            background: "radial-gradient(circle, rgba(255, 209, 102, 0.4) 0%, rgba(255, 209, 102, 0) 70%)",
            animation: "float 20s ease-in-out infinite",
          }}
        ></div>
        <div
          className="absolute bottom-20 right-[15%] w-[500px] h-[500px] rounded-full opacity-15 blur-3xl -z-10"
          style={{
            background: "radial-gradient(circle, rgba(74, 78, 105, 0.3) 0%, rgba(74, 78, 105, 0) 70%)",
            animation: "float 25s ease-in-out infinite reverse",
          }}
        ></div>

        <div className="w-full max-w-[972px] flex flex-col items-center justify-center relative z-10">
          <h1
            className="text-xl md:text-2xl lg:text-3xl font-bold text-[#4A4E69] text-center mb-3"
            style={{
              animation: "fadeInUp 0.5s ease-out forwards",
            }}
          >
            Olá! Este é seu espaço seguro.
          </h1>

          <p
            className="text-base md:text-lg text-[#272D37]/70 text-center mb-8 md:mb-12"
            style={{
              animation: "fadeInUp 0.5s ease-out 0.1s forwards",
            }}
          >
            O que está em sua mente no momento?
          </p>

          {/* Glassmorphic Input */}
          <div
            className="w-full max-w-[719px] h-14 rounded-full border border-[#D8DEE9]/80 bg-white/60 backdrop-blur-xl flex items-center px-4 gap-3 cursor-pointer mx-4 transition-all duration-300 hover:shadow-2xl hover:border-[#FFD166]/50 hover:-translate-y-1 group"
            onClick={() => setIsDialogOpen(true)}
            role="button"
            aria-label="Abrir chat com a KopfAI"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setIsDialogOpen(true);
              }
            }}
            style={{
              boxShadow: "0 10px 40px rgba(74, 78, 105, 0.08)",
              animation: "fadeInUp 0.5s ease-out 0.15s forwards",
            }}
          >
            <Mic
              className="w-5 h-5 text-[#4A4E69] flex-shrink-0 transition-all duration-300 group-hover:text-[#FFD166] group-hover:scale-110"
              aria-hidden="true"
            />
            <input
              type="text"
              placeholder="Digite sua mensagem..."
              className="flex-1 bg-transparent text-base text-[#272D37] placeholder:text-[#272D37]/80 outline-none cursor-pointer"
              readOnly
              aria-label="Campo de mensagem - clique para começar a conversar"
            />
            <Button
              size="icon"
              className="w-8 h-8 rounded-full bg-[#FFD166] hover:bg-[#FFD166]/90 flex-shrink-0 shadow-lg transition-all duration-300 hover:scale-110"
              onClick={(e) => {
                e.stopPropagation();
                setIsDialogOpen(true);
              }}
              aria-label="Enviar mensagem"
              style={{
                boxShadow: "0 4px 15px rgba(255, 209, 102, 0.4)",
              }}
            >
              <Send className="w-4 h-4 text-[#4A4E69]" aria-hidden="true" />
            </Button>
          </div>
        </div>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translate(0, 0) scale(1); }
            50% { transform: translate(30px, 30px) scale(1.1); }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </section>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] left-[50%] right-auto translate-x-[-50%] bg-white/95 backdrop-blur-2xl border border-[#D8DEE9]/50 shadow-2xl">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <DialogTitle className="text-[#4A4E69]">Comece sua jornada com a KopfAI</DialogTitle>
            </div>
            <DialogDescription className="text-left text-[#272D37]/70">
              Para conversar com a KopfAI você precisa criar uma conta primeiro. Ganhe{" "}
              <span className="font-semibold text-[#4A4E69]">7 dias grátis</span> sem compromisso e sem a necessidade de
              cadastrar cartão de crédito!
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
            <Button
              onClick={handleRegister}
              className="order-1 sm:order-2 bg-[#4A4E69] hover:bg-[#4A4E69]/90 text-white rounded-full shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
              style={{
                boxShadow: "0 10px 25px rgba(74, 78, 105, 0.15)",
              }}
            >
              Registrar agora
            </Button>
            <Button
              variant="link"
              onClick={handleLogin}
              className="underline p-0 h-auto font-normal text-sm order-2 sm:order-1 text-[#4A4E69] hover:text-[#272D37]"
            >
              Já possuo conta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
